import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../components/api';
import { useToast } from '../components/ToastContext';
import HuismeesterInstall from '../components/HuismeesterInstall';
import '../css/SchilderPortaal.css';

const API_BASE = process.env.REACT_APP_API_URL || '';
const WEEKNORM = 16;

const todayISO = () => new Date().toISOString().slice(0, 10);
const STATUS_LABELS = { te_plannen: 'Te plannen', gepland: 'Gepland', bezig: 'Bezig', afgerond: 'Afgerond' };
const NL_MAANDEN = ['januari', 'februari', 'maart', 'april', 'mei', 'juni', 'juli', 'augustus', 'september', 'oktober', 'november', 'december'];
const NL_DAGEN = ['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo'];

// Foto-proxy (zelfde publieke endpoint als de opnamestaat gebruikt).
const fotoSrc = (fileId) => `${API_BASE}/api/inspections/photo/${fileId}`;
// 'YYYY-MM' -> 'juli 2026'
const fmtPlanmaand = (ym) => {
  if (!ym || typeof ym !== 'string') return '';
  const [j, m] = ym.split('-').map(Number);
  return (j && m) ? `${NL_MAANDEN[m - 1]} ${j}` : ym;
};
const OPEN_STATUS = ['te_plannen', 'gepland', 'bezig'];

const isoOf = (d) => d.toISOString().slice(0, 10);

// ---------------- Klussen ----------------
const KlussenTab = ({ toast }) => {
  const [klussen, setKlussen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState({});
  const [bijz, setBijz] = useState({});

  const fetch = useCallback(async () => {
    try { setLoading(true); const r = await api.get('/api/schilder/klussen'); setKlussen(r.data); }
    catch { toast.error('Kan klussen niet laden'); }
    finally { setLoading(false); }
  }, [toast]);
  useEffect(() => { fetch(); }, [fetch]);

  const setStatus = async (k, status, extra = {}) => {
    try { await api.patch(`/api/schilder/klus/${k._id}/status`, { status, ...extra }); toast.success('Bijgewerkt'); fetch(); }
    catch (e) { toast.error(e.response?.data?.message || 'Mislukt'); }
  };
  const setStart = async (k, startDatum) => {
    try { await api.patch(`/api/schilder/klus/${k._id}/status`, { startDatum, status: k.status === 'te_plannen' ? 'gepland' : k.status }); fetch(); }
    catch { toast.error('Mislukt'); }
  };
  const uploadFoto = async (k, fase, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append('foto', file); fd.append('fase', fase);
    try { await api.post(`/api/schilder/klus/${k._id}/foto`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); toast.success(`Foto (${fase}) opgeslagen`); fetch(); }
    catch { toast.error('Upload mislukt'); }
  };
  const meldBijz = async (k) => {
    const tekst = (bijz[k._id] || '').trim();
    if (!tekst) return;
    try { await api.post(`/api/schilder/klus/${k._id}/bijzonderheid`, { tekst }); setBijz(p => ({ ...p, [k._id]: '' })); toast.success('Bijzonderheid gemeld'); fetch(); }
    catch { toast.error('Mislukt'); }
  };

  if (loading) return <p className="hp-muted">Laden…</p>;
  if (klussen.length === 0) return <p className="hp-muted">Geen klussen.</p>;

  const eersteAfgerond = klussen.findIndex(k => !OPEN_STATUS.includes(k.status));

  return (
    <div className="hp-klussen">
      {klussen.map((k, idx) => {
        const isAfgerond = !OPEN_STATUS.includes(k.status);
        const fotosVoor = k.fotosVoor || [];
        return (
        <React.Fragment key={k._id}>
          {idx === eersteAfgerond && eersteAfgerond > 0 && (
            <div className="hp-lijst-kop afgerond">✓ Afgeronde klussen</div>
          )}
        <div className={`hp-klus status-${k.status}`}>
          <div className="hp-klus-head" onClick={() => setOpen(p => ({ ...p, [k._id]: !p[k._id] }))}>
            <div>
              <span className="hp-huisnr">{k.huisnummer}</span>
              <span className="hp-naam">{k.bewoner?.naam}</span>
            </div>
            <div className="hp-klus-meta">
              <span className={`hp-status ${k.status}`}>{STATUS_LABELS[k.status] || k.status}</span>
              {!isAfgerond && k.prioriteit?.urgentie != null && <span className="hp-urg">urg {k.prioriteit.urgentie}</span>}
            </div>
          </div>

          {open[k._id] && (
            <div className="hp-klus-body">
              {k.planningsmaand && <p className="hp-aangekondigd">📅 Aangekondigd voor <strong>{fmtPlanmaand(k.planningsmaand)}</strong></p>}
              {k.bewoner?.telefoon && <p>📞 <a href={`tel:${k.bewoner.telefoon}`}>{k.bewoner.telefoon}</a></p>}
              {k.bewoner?.email && <p>✉ {k.bewoner.email}</p>}
              {k.prioriteit?.ernstigsteVeld && k.prioriteit.ernstigsteScore != null && k.prioriteit.ernstigsteScore < 5 && (
                <p className="hp-aandacht">⚠ Let op: {k.prioriteit.ernstigsteVeld}
                  <span className="hp-muted"> — opnamecijfer {k.prioriteit.ernstigsteScore}/5 (5 = goed, 1 = slecht)</span>
                </p>
              )}
              {k.aiAdvies?.aanbevolenBehandeling && <p className="hp-muted"><em>Advies: {k.aiAdvies.aanbevolenBehandeling}</em></p>}

              {fotosVoor.some(f => f.driveFileId) && (
                <div className="hp-thumbs">
                  <span className="hp-thumbs-label">Foto's vóór</span>
                  <div className="hp-thumbs-row">
                    {fotosVoor.filter(f => f.driveFileId).map((f, i) => (
                      <a key={i} href={f.driveUrl || fotoSrc(f.driveFileId)} target="_blank" rel="noopener noreferrer">
                        <img className="hp-thumb" src={fotoSrc(f.driveFileId)} alt={`Voor ${i + 1}`} loading="lazy" />
                      </a>
                    ))}
                  </div>
                  <p className="hp-scroll-hint">↓ scroll naar beneden om een bijzonderheid te melden</p>
                </div>
              )}

              <label className="hp-field">Startdatum (samen met bewoner)
                <input type="date" defaultValue={k.startDatum || ''} onBlur={e => e.target.value && setStart(k, e.target.value)} />
              </label>

              <div className="hp-statusbtns">
                <button className={k.status === 'bezig' ? 'active' : ''} onClick={() => setStatus(k, 'bezig')}>Ik begin hier</button>
                <button className={k.status === 'afgerond' ? 'active' : ''} onClick={() => setStatus(k, 'afgerond')}>Afgerond</button>
              </div>

              <div className="hp-fotos">
                <div className="hp-foto-col">
                  <span>Foto's vóór ({(k.fotosVoor || []).length})</span>
                  <label className="hp-foto-btn">📷 Toevoegen
                    <input type="file" accept="image/*" capture="environment" hidden onChange={e => uploadFoto(k, 'voor', e.target.files[0])} />
                  </label>
                </div>
                <div className="hp-foto-col">
                  <span>Foto's ná ({(k.fotosNa || []).length})</span>
                  <label className="hp-foto-btn">📷 Toevoegen
                    <input type="file" accept="image/*" capture="environment" hidden onChange={e => uploadFoto(k, 'na', e.target.files[0])} />
                  </label>
                </div>
              </div>

              <label className="hp-field">Bijzonderheid melden
                <textarea rows={2} placeholder="Bijv. houtrot aangetroffen, balkon niet bereikbaar…" value={bijz[k._id] || ''} onChange={e => setBijz(p => ({ ...p, [k._id]: e.target.value }))} />
              </label>
              <button className="hp-btn" onClick={() => meldBijz(k)}>Bijzonderheid versturen</button>
              {(k.bijzonderheden || []).length > 0 && (
                <ul className="hp-bijz-list">
                  {k.bijzonderheden.map((b, i) => <li key={i}>{b.tekst} <span className="hp-muted">({new Date(b.op).toLocaleDateString('nl-NL')})</span></li>)}
                </ul>
              )}

              <button className="hp-terug" onClick={() => setOpen(p => ({ ...p, [k._id]: false }))}>← Terug naar klussen</button>
            </div>
          )}
        </div>
        </React.Fragment>
        );
      })}
    </div>
  );
};

// ---------------- Uren & saldo ----------------
const SOORT_LABELS = { gewerkt: 'Gewerkt', vakantie: 'Vakantie', ziekte: 'Ziek', overig: 'Overig' };

const UrenTab = ({ toast, klussen }) => {
  const [data, setData] = useState({ rows: [], starturen: 0, startDatum: null, weeknorm: WEEKNORM, huidigSaldo: 0, totaalUren: 0 });
  const [form, setForm] = useState({ datum: todayISO(), uren: '', huisnummer: '', omschrijving: '' });
  const [startForm, setStartForm] = useState({ starturen: '', startDatum: '' });
  const [startOpen, setStartOpen] = useState(false);

  const fetch = useCallback(async () => {
    try {
      const r = await api.get('/api/schilder/urenoverzicht');
      setData(r.data);
      setStartForm({ starturen: String(r.data.starturen ?? 0), startDatum: r.data.startDatum || '' });
    } catch { toast.error('Kan urenoverzicht niet laden'); }
  }, [toast]);
  useEffect(() => { fetch(); }, [fetch]);

  const bewaarStart = async () => {
    try {
      await api.put('/api/schilder/starturen', { starturen: Number(startForm.starturen) || 0, startDatum: startForm.startDatum || null });
      toast.success('Starturen opgeslagen'); setStartOpen(false); fetch();
    } catch { toast.error('Opslaan mislukt'); }
  };
  const voegToe = async () => {
    if (!form.datum || !form.uren) { toast.error('Datum en uren invullen'); return; }
    try { await api.post('/api/schilder/uren', { ...form, uren: Number(form.uren) }); setForm({ datum: todayISO(), uren: '', huisnummer: '', omschrijving: '' }); toast.success('Uren opgeslagen'); fetch(); }
    catch { toast.error('Opslaan mislukt'); }
  };
  const verwijder = async (row) => {
    const url = row.bron === 'afwezigheid' ? `/api/schilder/afwezigheid/${row._id}` : `/api/schilder/uren/${row._id}`;
    try { await api.delete(url); fetch(); } catch { toast.error('Verwijderen mislukt'); }
  };

  const { rows, starturen, startDatum, weeknorm, huidigSaldo } = data;
  const saldoKlasse = huidigSaldo > 0 ? 'plus' : huidigSaldo < 0 ? 'min' : '';

  return (
    <div className="hp-uren">
      {/* Huidig saldo */}
      <div className={`hp-saldobar ${saldoKlasse}`}>
        <span>Huidig saldo</span>
        <strong>{huidigSaldo > 0 ? '+' : ''}{huidigSaldo} u</strong>
        <span className="hp-muted">norm {weeknorm} u/week</span>
      </div>

      {/* Starturen (begin-saldo) */}
      <div className="hp-card">
        <div className="hp-card-head" onClick={() => setStartOpen(o => !o)}>
          <h3 style={{ margin: 0 }}>Starturen</h3>
          <span className="hp-muted">{startDatum ? `${starturen > 0 ? '+' : ''}${starturen} u vanaf ${startDatum}` : 'nog niet ingesteld'} ▾</span>
        </div>
        {startOpen && (
          <div style={{ marginTop: 12 }}>
            <p className="hp-muted">Het begin-saldo (+/-) op de startdatum. Vanaf die datum loopt de {weeknorm} u/week-norm.</p>
            <label className="hp-field">Startdatum<input type="date" value={startForm.startDatum} onChange={e => setStartForm({ ...startForm, startDatum: e.target.value })} /></label>
            <label className="hp-field">Starturen (+/-)<input type="number" step="0.25" value={startForm.starturen} onChange={e => setStartForm({ ...startForm, starturen: e.target.value })} /></label>
            <button className="hp-btn primary" onClick={bewaarStart}>Opslaan</button>
          </div>
        )}
      </div>

      {/* Uren toevoegen */}
      <div className="hp-card">
        <h3>Uren toevoegen</h3>
        <label className="hp-field">Datum<input type="date" value={form.datum} onChange={e => setForm({ ...form, datum: e.target.value })} /></label>
        <label className="hp-field">Aantal uren<input type="number" step="0.25" min="0" value={form.uren} onChange={e => setForm({ ...form, uren: e.target.value })} /></label>
        <label className="hp-field">Woning (optioneel)
          <select value={form.huisnummer} onChange={e => setForm({ ...form, huisnummer: e.target.value })}>
            <option value="">— geen —</option>
            {klussen.map(k => <option key={k._id} value={k.huisnummer}>{k.huisnummer} {k.bewoner?.naam || ''}</option>)}
          </select>
        </label>
        <label className="hp-field">Omschrijving<input type="text" value={form.omschrijving} onChange={e => setForm({ ...form, omschrijving: e.target.value })} /></label>
        <button className="hp-btn primary" onClick={voegToe}>Opslaan</button>
        <p className="hp-muted" style={{ marginTop: 8 }}>Vakantie en ziek voer je in via het tabblad Vakantie; ze verschijnen hieronder vanzelf in de lijst.</p>
      </div>

      {/* Totaallijst met doorlopend saldo */}
      <div className="hp-card">
        <h3>Totaaloverzicht</h3>
        {(!rows || rows.length === 0) ? <p className="hp-muted">Nog niets geregistreerd.</p> : (
          <div className="hp-saldolijst">
            <div className="hp-saldorow head">
              <span>Datum</span><span>Soort</span><span className="r">Uren</span><span className="r">Saldo</span><span />
            </div>
            {rows.map(r => (
              <div key={`${r.bron}-${r._id}`} className={`hp-saldorow soort-${r.soort}`}>
                <span>{r.datum}</span>
                <span>
                  <span className={`hp-soort ${r.soort}`}>{SOORT_LABELS[r.soort] || r.soort}</span>
                  {(r.huisnummer || r.omschrijving) && <span className="hp-saldo-detail">{r.huisnummer ? `#${r.huisnummer} ` : ''}{r.omschrijving}</span>}
                </span>
                <span className="r"><strong>{r.uren} u</strong></span>
                <span className={`r hp-saldo-cel ${r.saldo > 0 ? 'plus' : r.saldo < 0 ? 'min' : ''}`}>{r.saldo > 0 ? '+' : ''}{r.saldo}</span>
                {r.status !== 'goedgekeurd'
                  ? <button className="hp-del" onClick={() => verwijder(r)}>✕</button>
                  : <span title="goedgekeurd">🔒</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ---------------- Vakantie & afwezigheid ----------------
const VakantieTab = ({ toast }) => {
  const [saldo, setSaldo] = useState(null);
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ type: 'vakantie', vanDatum: todayISO(), totDatum: todayISO(), dagdeel: 'hele_dag', opmerking: '' });

  const fetch = useCallback(async () => {
    try {
      const [s, a] = await Promise.all([api.get('/api/schilder/saldo'), api.get('/api/schilder/afwezigheid')]);
      setSaldo(s.data); setItems(a.data);
    } catch { toast.error('Kan vakantiegegevens niet laden'); }
  }, [toast]);
  useEffect(() => { fetch(); }, [fetch]);

  const voegToe = async (override) => {
    const payload = { ...form, ...(override || {}) };
    try { await api.post('/api/schilder/afwezigheid', payload); toast.success('Geregistreerd'); fetch(); }
    catch (e) { toast.error(e.response?.data?.message || 'Mislukt'); }
  };
  const ziekVandaag = () => voegToe({ type: 'ziekte', vanDatum: todayISO(), totDatum: todayISO(), dagdeel: 'hele_dag' });
  const verwijder = async (id) => { try { await api.delete(`/api/schilder/afwezigheid/${id}`); fetch(); } catch { toast.error('Mislukt'); } };

  return (
    <div className="hp-vakantie">
      {saldo && (
        <div className="hp-saldo">
          <div className="hp-saldo-cijfer"><strong>{saldo.gebruiktDagen}</strong> / {saldo.budgetDagen} dagen</div>
          <div className="hp-weekbalk"><span style={{ width: `${Math.min(100, (saldo.gebruiktUren / saldo.budgetUren) * 100)}%`, background: '#e67e22' }} /></div>
          <span className="hp-muted">{saldo.restUren} u vakantie resterend</span>
        </div>
      )}

      <button className="hp-btn ziek" onClick={ziekVandaag}>🤒 Vandaag ziek melden</button>

      <div className="hp-card">
        <h3>Vakantie / afwezigheid melden</h3>
        <label className="hp-field">Type
          <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
            <option value="vakantie">Vakantie</option>
            <option value="ziekte">Ziekte</option>
            <option value="overig">Overig</option>
          </select>
        </label>
        <label className="hp-field">Van<input type="date" value={form.vanDatum} onChange={e => setForm({ ...form, vanDatum: e.target.value })} /></label>
        <label className="hp-field">Tot en met<input type="date" value={form.totDatum} onChange={e => setForm({ ...form, totDatum: e.target.value })} /></label>
        <label className="hp-field">Dagdeel
          <select value={form.dagdeel} onChange={e => setForm({ ...form, dagdeel: e.target.value })}>
            <option value="hele_dag">Hele dag(en)</option>
            <option value="ochtend">Ochtend</option>
            <option value="middag">Middag</option>
          </select>
        </label>
        <label className="hp-field">Opmerking<input type="text" value={form.opmerking} onChange={e => setForm({ ...form, opmerking: e.target.value })} /></label>
        <button className="hp-btn primary" onClick={() => voegToe()}>Melden</button>
      </div>

      <div className="hp-card">
        <h3>Geregistreerd</h3>
        {items.length === 0 ? <p className="hp-muted">Niets geregistreerd.</p> : items.map(a => (
          <div key={a._id} className={`hp-afw-row ${a.type}`}>
            <span className="hp-afw-type">{a.type}</span>
            <span>{a.vanDatum}{a.totDatum !== a.vanDatum ? ` t/m ${a.totDatum}` : ''}</span>
            <span className="hp-muted">{a.urenGeblokkeerd} u · {a.status}</span>
            <button className="hp-del" onClick={() => verwijder(a._id)}>✕</button>
          </div>
        ))}
      </div>
    </div>
  );
};

// ---------------- Planning (kalender) ----------------
const PlanningTab = ({ toast }) => {
  const [ref, setRef] = useState(new Date());
  const [uren, setUren] = useState([]);
  const [afw, setAfw] = useState([]);

  const jaar = ref.getFullYear();
  const maand = ref.getMonth();

  const fetch = useCallback(async () => {
    const van = isoOf(new Date(jaar, maand, 1));
    const tot = isoOf(new Date(jaar, maand + 1, 0));
    try {
      const [u, a] = await Promise.all([
        api.get(`/api/schilder/uren?van=${van}&tot=${tot}`),
        api.get('/api/schilder/afwezigheid')
      ]);
      setUren(u.data.uren || []);
      setAfw(a.data || []);
    } catch { toast.error('Kan planning niet laden'); }
  }, [jaar, maand, toast]);
  useEffect(() => { fetch(); }, [fetch]);

  const urenPerDag = {};
  uren.forEach(u => { urenPerDag[u.datum] = (urenPerDag[u.datum] || 0) + (u.uren || 0); });
  const maandTotaal = Math.round(uren.reduce((s, u) => s + (u.uren || 0), 0) * 10) / 10;
  const afwType = (iso) => {
    const hit = afw.find(a => iso >= a.vanDatum && iso <= a.totDatum);
    return hit ? hit.type : null;
  };

  const eersteDag = (new Date(jaar, maand, 1).getDay() + 6) % 7; // ma=0
  const dagenInMaand = new Date(jaar, maand + 1, 0).getDate();
  const cellen = [];
  for (let i = 0; i < eersteDag; i++) cellen.push(null);
  for (let d = 1; d <= dagenInMaand; d++) cellen.push(d);

  return (
    <div className="hp-planning">
      <div className="hp-maandnav">
        <button onClick={() => setRef(new Date(jaar, maand - 1, 1))}>‹</button>
        <strong>{NL_MAANDEN[maand]} {jaar}</strong>
        <button onClick={() => setRef(new Date(jaar, maand + 1, 1))}>›</button>
      </div>
      <div className="hp-maandtotaal hp-muted">Gewerkt deze maand: <strong>{maandTotaal} u</strong></div>
      <div className="hp-kal-head">{NL_DAGEN.map(d => <span key={d}>{d}</span>)}</div>
      <div className="hp-kal">
        {cellen.map((d, i) => {
          if (!d) return <span key={i} className="hp-kal-cel leeg" />;
          const iso = isoOf(new Date(jaar, maand, d));
          const t = afwType(iso);
          const u = urenPerDag[iso];
          return (
            <span key={i} className={`hp-kal-cel ${t ? 'afw-' + t : ''} ${iso === todayISO() ? 'vandaag' : ''}`}>
              <span className="hp-kal-dag">{d}</span>
              {u ? <span className="hp-kal-uren">{u}u</span> : null}
              {t && <span className="hp-kal-merk">{t === 'vakantie' ? 'vak' : t === 'ziekte' ? 'ziek' : 'afw'}</span>}
            </span>
          );
        })}
      </div>
      <div className="hp-legenda">
        <span><i className="dot vak" /> vakantie</span>
        <span><i className="dot ziek" /> ziek</span>
        <span><i className="dot werk" /> gewerkte uren</span>
      </div>
    </div>
  );
};

// ---------------- Portaal-shell ----------------
const SchilderPortaal = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const [tab, setTab] = useState('klussen');
  const [me, setMe] = useState(null);
  const [geenToegang, setGeenToegang] = useState(false);
  const [klussen, setKlussen] = useState([]);

  useEffect(() => {
    api.get('/api/schilder/me')
      .then(r => setMe(r.data))
      .catch(err => { if (err.response?.status === 403) setGeenToegang(true); });
    api.get('/api/schilder/klussen').then(r => setKlussen(r.data)).catch(() => {});
  }, []);

  const logout = () => {
    localStorage.removeItem('token'); localStorage.removeItem('refreshToken'); localStorage.removeItem('loginTimestamp');
    navigate('/login');
  };

  if (geenToegang) {
    return <div className="hp-page"><div className="hp-top"><h1>Geen toegang</h1></div><p className="hp-muted" style={{ padding: 20 }}>Dit portaal is alleen voor de huismeester. <button className="hp-btn" onClick={logout}>Uitloggen</button></p></div>;
  }

  return (
    <div className="hp-page">
      <header className="hp-top">
        <h1>Huismeester</h1>
        <button className="hp-logout" onClick={logout}>Uitloggen</button>
      </header>

      <main className="hp-main">
        <HuismeesterInstall />
        {tab === 'klussen' && <KlussenTab toast={toast} />}
        {tab === 'uren' && <UrenTab toast={toast} klussen={klussen} />}
        {tab === 'vakantie' && <VakantieTab toast={toast} />}
        {tab === 'planning' && <PlanningTab toast={toast} />}
      </main>

      <nav className="hp-tabbar">
        <button className={tab === 'klussen' ? 'active' : ''} onClick={() => setTab('klussen')}><span>🪜</span>Klussen</button>
        <button className={tab === 'uren' ? 'active' : ''} onClick={() => setTab('uren')}><span>⏱</span>Uren</button>
        <button className={tab === 'vakantie' ? 'active' : ''} onClick={() => setTab('vakantie')}><span>🏖</span>Vakantie</button>
        <button className={tab === 'planning' ? 'active' : ''} onClick={() => setTab('planning')}><span>📅</span>Agenda</button>
      </nav>
    </div>
  );
};

export default SchilderPortaal;
